<template>
    <div class="cb-chatarea" @scroll="handleMessageScroll">
        <div class="cb-message-body">
            <Loading :active="getItem('loadChatMsgsProcessing')"
                :is-full-page="false" loader="spinner" color='black' :width="40" :height="40"
                :can-cancel="false"
                :lock-scroll="true"
                backgroundColor="none"
                :opacity="0.9"
                :z-index=999
            />
            <template v-for="(message, index) in getItem('chatMessages')">
                <div :class="['cb-message-wrapper', message.role]" :key="index">
                    <div v-if="message.content=='subscription_chat_limit_exceeded'" class="cb-message"> 
                        <div class="message-text">{{ $t('subscription_chat_limit_exceeded') }}</div>
                    </div>  
                    <div v-if="message.content=='user_chat_limit_exceeded'" class="cb-message"> 
                        <div class="message-text">{{ $t('user_chat_limit_exceeded') }}</div>
                    </div>  
                    <div v-if="message.content=='openai_could_not_process_request'" class="cb-message"> 
                        <div class="message-text">{{ $t('openai_could_not_process_request') }}</div>
                    </div>  
                    <div v-if="message.role=='assistant'" class="cb-message">
                        <div class="message-text" v-html="renderAssistantMessage(message)" :id="'chat-message-' + message.id" v-mathjax></div>
                        <div :class="['message-action', getItem('chatMessages').length-1 == index ? 'show' : '']">
                            <div :class="['message-like', 'ai-tooltip-bottom', (message.rating === 1)? 'active' : '']" :data-tooltip="$t('frm_ai_good_response')" @click.prevent="handleChatMessageRating(message.id, message.rating, 1)"></div>
                            <div :class="['message-dislike', 'ai-tooltip-bottom', (message.rating === 0)? 'active' : '']"  :data-tooltip="$t('frm_ai_bad_response')" @click.prevent="handleChatMessageRating(message.id, message.rating, 0)"></div>
                            <div :class="['message-copy', 'ai-tooltip-bottom',  (copiedAnswer == index) ? 'message-copied': '']" :data-tooltip="$t('frm_copy')"  @click.prevent="copyToClipboard('answer', message.content, index)"></div>
                        </div>
                    </div>
                    <div v-if="message.role=='user'" class="cb-message">
                        <div :class="['message-action']">
                            <div :class="['message-copy', 'ai-tooltip-bottom', (copiedQuestion == index) ? 'message-copied': '']" :data-tooltip="$t('frm_copy')" @click.prevent="copyToClipboard('question', message.content, index)"></div>
                        </div>
                        <div class="message-text">{{ message.content }}</div>
                    </div>
                </div>
            </template>
            <div v-if="getItem('waitingReply')" class="cb-message-wrapper assistant">
                <div class="cb-message">
                    <div class="message-text loading"></div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import Loading from 'vue-loading-overlay';
    import VueShowdown from 'vue-showdown';
    import DOMPurify from 'dompurify';
    import "@/directives/mathjaxDirective";
    import { createNamespacedHelpers } from 'vuex';

    const markdownConverter = new VueShowdown.showdown.Converter({
        tables: true,
        simplifiedAutoLink: true,
        strikethrough: true,
        tasklists: true,
        ghCompatibleHeaderId: true,
        simpleLineBreaks: true,
        openLinksInNewWindow: true
    });
    markdownConverter.setFlavor('github');

    const SANITIZE_ALLOWED_TAGS = [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'strong', 'em', 'a', 'sup', 'div', 'span',
        'code', 'pre', 'blockquote'
    ];
    const SANITIZE_ALLOWED_ATTR = ['href', 'target', 'rel', 'class'];
    const SANITIZE_ALLOWED_CLASSES = ['sup-citation', 'citation', 'citation-link', 'citation-row', 'citation-index', 'citation-title'];

    const { mapActions: aiChatMapActions, mapGetters: aiChatMapGetters } = createNamespacedHelpers('aichat')
    export default {
        name: "ChatMessaging",
        components: {
            Loading
        },
        data() {
            return {
                messageSrollTop: 0,
                copiedAnswer: -1,
                copiedQuestion: -1
            }
        },
        computed: {
            ...aiChatMapGetters([
                'getItem'
            ]),
        },
        methods: {
            ...aiChatMapActions([
                'setItem',
                'rateChatMessage',
                'loadMoreMessages'
            ]),
            async copyToClipboard(type, content, itemIndex) {
                try {
                    if(type == 'answer') {
                        this.copiedAnswer = itemIndex;
                        this.copiedQuestion = -1;
                    }
                    if(type == 'question') {
                        this.copiedAnswer = -1;
                        this.copiedQuestion = itemIndex;
                    }
                    this.copiedResponse = itemIndex;
                    await navigator.clipboard.writeText(content);
                    setTimeout(() => {
                        this.copiedAnswer = -1;
                        this.copiedQuestion = -1;
                    }, 2000);
                } catch (error) {
                    console.error('Unable to copy to clipboard:', error);
                }
            },
            handleChatMessageRating(messageId, currentRating, rating) {
                if(currentRating === rating) {
                    rating = '';
                }
                this.rateChatMessage({messageId, rating});
            },
            renderAssistantMessage: function(msg) {
                const messageWithCitation = this.buildMessageWithCitation(msg);
                const markdownHtml = markdownConverter.makeHtml(messageWithCitation);
                const sanitizedHtml = DOMPurify.sanitize(markdownHtml, {
                    ALLOWED_TAGS: SANITIZE_ALLOWED_TAGS,
                    ALLOWED_ATTR: SANITIZE_ALLOWED_ATTR
                });
                return this.hardenTargetBlankLinks(sanitizedHtml);
            },
            buildMessageWithCitation: function(msg) {
                if (!msg || typeof msg.content !== 'string') {
                    return '';
                }
                if (!Array.isArray(msg.citations) || msg.citations.length === 0) {
                    return msg.content;
                }

                const docId = this.getItem('docId');
                const itemId = this.getItem('itemId');
                const resId = this.getItem('resId');
                const pubId = this.getItem('pubId');
                const message = {...msg}; // Keep source message immutable for render cycle safety.
                let messageContent = message.content;
                let citationLinks = "";
                let citationSeqNo = 1;

                const foundCitations = [];
                messageContent.replace(/\[doc(\d+)\]/gi, function(match, docIndex)  {
                    foundCitations.push(parseInt(docIndex, 10));
                    return match;
                });

                const uniqueCitations = [...new Set(foundCitations.filter(Number.isFinite))].sort(function(a, b) {
                    return a - b;
                });

                uniqueCitations.forEach((citationDocumentIndex) => {
                    const citationToken = "\\[doc" + citationDocumentIndex + "\\]";
                    const citationRegex = new RegExp(citationToken, "gi");
                    messageContent = messageContent.replace(citationRegex, '<sup class="citation">' + citationSeqNo + '</sup>');

                    const citationIndex = citationDocumentIndex - 1;
                    const citation = message.citations[citationIndex];
                    if (!citation) {
                        citationSeqNo++;
                        return;
                    }

                    let page = 0;
                    if (citation.metadata && citation.metadata.page !== undefined) {
                        page = parseInt(citation.metadata.page, 10);
                    } else if (citation.title && citation.title.page !== undefined) {
                        page = parseInt(citation.title.page, 10);
                    }
                    if (isNaN(page)) {
                        page = 0;
                    }
                    page = page + 1;

                    let citationArray = [];
                    if (citation.content) {
                        try {
                            const decodedString = atob(citation.content);
                            const parsedCitationArray = JSON.parse(decodedString);
                            if (Array.isArray(parsedCitationArray)) {
                                citationArray = parsedCitationArray;
                            }
                        } catch (error) {
                            citationArray = [];
                        }
                    }

                    const strCitation = this.escapeHtml(citationArray.join(' ')).trim();
                    const citationSuffix = strCitation !== "" ? strCitation : '...';
                    citationLinks += '<div class="citation-row"><span class="citation-index">' + citationSeqNo + '</span><a class="citation-link" target="_blank" href="/management/display/citations/' + docId + '/' + itemId + '/' + resId + '/' + pubId + '/' + page + '">Page ' + page + ', ' + citationSuffix + '</a></div>';
                    citationSeqNo++;
                });

                if(citationLinks) {
                    messageContent = messageContent + '<br /><br /><div class="citation-title">References:</div>' + citationLinks;
                }

                const citationPattern = /(<sup class="citation">\d+<\/sup>)+/g;
                messageContent = messageContent.replace(citationPattern, function(match) {
                    return '<span class="sup-citation">' + match + '</span>';
                });

                return messageContent;
            },
            hardenTargetBlankLinks: function(html) {
                if (typeof document === 'undefined') {
                    return html;
                }
                const container = document.createElement('div');
                container.innerHTML = html;
                container.querySelectorAll('*[class]').forEach(function(element) {
                    const allowedClasses = element.className
                        .split(/\s+/)
                        .filter(function(className) {
                            return SANITIZE_ALLOWED_CLASSES.includes(className);
                        });
                    if (allowedClasses.length > 0) {
                        element.className = allowedClasses.join(' ');
                    } else {
                        element.removeAttribute('class');
                    }
                });
                container.querySelectorAll('a[target="_blank"]').forEach(function(link) {
                    const href = (link.getAttribute('href') || '').trim();
                    // Keep legacy behavior for internal citation deep-links so viewer page navigation
                    // keeps working exactly as before.
                    if (href.indexOf('/management/display/citations/') === 0) {
                        link.removeAttribute('rel');
                        return;
                    }
                    link.setAttribute('rel', 'noopener noreferrer nofollow');
                });
                return container.innerHTML;
            },
            escapeHtml: function(value) {
                return String(value)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
            },
            handleMessageScroll: function(el) {
                if(this.messageSrollTop == 0) {
                    this.messageSrollTop = el.srcElement.scrollTop;
                }
                if(el.srcElement.scrollTop < 20) {
                    if(this.messageSrollTop >  el.srcElement.scrollTop && !this.getItem('loadMoreMsgsProcessing')) {
                        if(this.getItem('chatMessages').length>0 && (this.getItem('chatMessages')[0].id && this.getItem('chatMessages')[0].role !='system')) {
                            if(this.getItem('chatId')) {
                                this.loadMoreMessages(this.getItem('chatId'));
                            }
                        }
                    }
                }
                this.messageSrollTop = el.srcElement.scrollTop;
            }
        }
    }
</script>
